import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Keep a module tab in the URL (`?tab=`) so refresh / back stays on the same tab.
 * Other query params (patientId, page, etc.) are preserved.
 *
 * @param {string[]} allowedIds
 * @param {string} fallback
 * @param {string} [param]
 */
export function useUrlTab(allowedIds, fallback, param = "tab") {
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedKey = Array.isArray(allowedIds)
    ? allowedIds.filter(Boolean).join("\0")
    : "";
  const allowed = useMemo(
    () => new Set(allowedKey ? allowedKey.split("\0") : []),
    [allowedKey],
  );

  const raw = searchParams.get(param);
  const activeTab = allowed.has(raw) ? raw : fallback;

  const setActiveTab = useCallback(
    (tab) => {
      const nextId = allowed.has(tab) ? tab : fallback;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (nextId) next.set(param, nextId);
          else next.delete(param);
          return next;
        },
        { replace: true },
      );
    },
    [allowed, fallback, param, setSearchParams],
  );

  return [activeTab, setActiveTab];
}

/** Merge keys into the current search string without dropping other params. */
export function patchSearchParams(searchParams, patch) {
  const next = new URLSearchParams(searchParams);
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value == null || value === "") next.delete(key);
    else next.set(key, String(value));
  });
  return next;
}

/** Apply query patches to a relative path (preserves existing params and hash). */
export function withSearchParams(href, patch) {
  if (!href || typeof href !== "string") return href;
  const hashIndex = href.indexOf("#");
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : "";
  const qIndex = withoutHash.indexOf("?");
  const pathname = qIndex >= 0 ? withoutHash.slice(0, qIndex) : withoutHash;
  const search = qIndex >= 0 ? withoutHash.slice(qIndex + 1) : "";
  const next = patchSearchParams(new URLSearchParams(search), patch);
  const qs = next.toString();
  return `${pathname}${qs ? `?${qs}` : ""}${hash}`;
}
